import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { BillingSummary } from '@lizrd/contracts';
import { getBillingSummary, getOrgSavingsSummary, startStripeCheckout } from '../lib/api';
import { useAuthStore, useIsSandbox } from '../store/auth';
import { usd } from '../lib/graph-visuals';
import { Button } from './ui/button';
import { Logo } from './brand/Logo';

/**
 * Whether the day-8 trial paywall should block the app. True only once the free
 * trial has ended AND payments are active without a saved card:
 *   - `deferred` = Stripe off (private beta) → never gated (beta has no billing)
 *   - `on_file`  = card saved → already a paying customer
 * So a beta org (deferred) is never blocked, satisfying "only when BETA is disabled".
 */
/** True on the local dev / docker sandbox stack (never on a deployed host). */
function isLocalHost(): boolean {
  if (typeof window === 'undefined') return false;
  return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
}

export function shouldShowTrialPaywall(
  summary: BillingSummary | undefined,
  isSandbox: boolean,
): boolean {
  if (isSandbox || !summary) return false;
  return !summary.trial.active && summary.paymentMethod.status === 'none';
}

/**
 * Hard paywall at the end of the 7-day free trial (reverse trial): once the trial
 * lapses and no card is on file, Lizrd is blocked by a non-dismissible modal that
 * shows what's waiting (open recommendations + monthly savings potential) and sends
 * the user to Stripe Checkout to continue. Never shown in the private beta (Stripe
 * off → paymentMethod 'deferred') or for the demo sandbox.
 */
/** The one route exempt from the paywall: the billing page is where the customer
 * adds a payment method, so it must stay reachable with the modal gone. */
const BILLING_PATH = '/settings/billing';

export function TrialPaywall() {
  const isSandbox = useIsSandbox();
  const logout = useAuthStore((s) => s.logout);
  const { pathname } = useLocation();

  // Re-evaluate without a page reload: this is a SPA, so poll + re-check on tab
  // focus, so a trial that lapses mid-session surfaces the paywall on its own.
  const billing = useQuery({
    queryKey: ['billing', 'summary'],
    queryFn: getBillingSummary,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const savings = useQuery({ queryKey: ['org-savings'], queryFn: getOrgSavingsSummary });

  // `trial.active` is computed server-side at fetch time, so a summary fetched
  // during the trial stays active:true until refetched. Fire a precise refetch at
  // the exact trial-end moment so the wall appears instantly at the boundary — not
  // up to a poll-interval later.
  const trialActive = billing.data?.trial.active;
  const trialEndsAt = billing.data?.trial.endsAt;
  const refetchBilling = billing.refetch;
  useEffect(() => {
    if (!trialActive || !trialEndsAt) return;
    const ms = new Date(trialEndsAt).getTime() - Date.now();
    if (ms <= 0) {
      void refetchBilling();
      return;
    }
    const id = setTimeout(() => void refetchBilling(), ms + 1000);
    return () => clearTimeout(id);
  }, [trialActive, trialEndsAt, refetchBilling]);

  const checkout = useMutation({
    mutationFn: startStripeCheckout,
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });

  // Local dev (the :8080 sandbox stack) turns Stripe ON to exercise the offline
  // fake gateway, which would otherwise trip this paywall for the seed's past-trial
  // org. Never block on localhost so the app stays fully testable locally — prod
  // hosts (app.lizrd.ai / eu.app.lizrd.ai) are never localhost, so they're untouched.
  if (import.meta.env.DEV || isLocalHost()) return null;

  // Let the customer reach billing to pay — never cover the billing page.
  if (pathname.startsWith(BILLING_PATH)) return null;
  if (!shouldShowTrialPaywall(billing.data, isSandbox)) return null;

  const openCount = savings.data?.totals.openCount ?? 0;
  const potential = savings.data?.totals.estimatedMonthlySaving ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-obsidian/80 p-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
    >
      <div className="w-full max-w-md rounded-xl border border-hairline bg-surface p-8 shadow-2xl">
        <div className="mb-5 flex justify-center">
          <Logo size={28} />
        </div>
        <h2 id="paywall-title" className="text-center font-display text-xl font-bold text-bone">
          Your free trial has ended
        </h2>
        <p className="mt-2 text-center text-sm leading-relaxed text-muted">
          Add a payment method to keep optimizing. You’re billed a small, predictable share of the
          spend Lizrd manages — nothing per-seat.
        </p>

        {/* What's waiting — the dashboard numbers, as the reason to continue. */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-hairline bg-surface-2 p-4 text-center">
            <div className="font-display text-2xl font-bold text-bone tnum">{openCount}</div>
            <div className="mt-0.5 text-xs text-muted">open recommendations</div>
          </div>
          <div className="rounded-lg border border-iris/30 bg-iris/5 p-4 text-center">
            <div className="font-display text-2xl font-bold text-iris tnum">
              {usd(potential)}
              <span className="text-sm font-medium text-muted">/mo</span>
            </div>
            <div className="mt-0.5 text-xs text-muted">savings potential</div>
          </div>
        </div>

        <Button
          className="mt-6 w-full"
          disabled={checkout.isPending}
          onClick={() => checkout.mutate()}
        >
          {checkout.isPending ? 'Opening checkout…' : 'Continue Optimizing →'}
        </Button>
        {checkout.isError && (
          <p className="mt-2 text-center text-xs text-danger">
            Couldn’t start checkout. Please try again.
          </p>
        )}
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-4 w-full text-center text-xs text-muted transition-colors hover:text-bone"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
